import React from "react";
import ReactDOMServer from "react-dom/server";
import { useTranslation } from "react-i18next";
import { css, styled } from "goober";

import { appURLs } from "@/app/app-urls.mjs";

const STAT_ICONS = {
  scaleAD: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleAD.png`,
  scaleAP: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleAP.png`,
  scaleArmor: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleArmor.png`,
  scaleAS: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleAS.png`,
  scaleCC: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleCC.png`,
  scaleCrit: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleCrit.png`,
  scaleCritMult: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleCritMult.png`,
  scaleDA: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleDA.png`,
  scaleDR: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleDR.png`,
  scaleDodge: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleDodge.png`,
  scaleHealth: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleHealth.png`,
  scaleMana: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleMana.png`,
  scaleMovement: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleMovement.png`,
  scaleMR: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleMR.png`,
  scaleRange: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleRange.png`,
  scaleSV: `${appURLs.CDN}/blitz/tft/stat-icons/tft-scaleSV.png`,
  set10Headliner: `${appURLs.CDN}/blitz/tft/stat-icons/tft-headliner+1.png`,
  set14AmpIcon: `${appURLs.CDN}/blitz/tft/stat-icons/tft-set14-amp+1.png`,
};

const textIcons = {};

const CustomTag = styled("span")`
  .physical-damage {
    color: #f3a722;
  }
  .magic-damage {
    color: #30d9d4;
  }
  .scale-health {
    color: #1dd25e;
  }
  .radiant-bonus {
    color: var(--yellow);
  }
  .headliner-bonus {
    color: #6ad264;
  }
  .rules {
    opacity: 0.5;
  }
  .bold {
    font-weight: bold;
  }
  .bonus,
  .spell-active,
  .spell-passive,
  .sacle-level,
  .keyword,
  .true-damage {
    color: var(--shade0);
  }
  .experiment-bonus {
    color: #477b59;
  }
`;

const ParsedDescription = ({ text, iconSize = "var(--sp-5)", ...rest }) => {
  const { t } = useTranslation();
  if (!text || typeof text !== "string") return "";

  let result = text.replace(/(<br>){2,}/g, "<br><br>").replace(/<br>$/, "");

  // Define replacements for custom tags
  const replaceTags = {
    physicalDamage: "physical-damage",
    magicDamage: "magic-damage",
    TFTBonus: "bonus",
    spellActive: "spell-active",
    spellPassive: "spell-passive",
    scaleLevel: "scale-level",
    TFTKeyword: "keyword",
    scaleHealth: "scale-health",
    rules: "rules",
    tftitemrules: "rules",
    tftbold: "bold",
    TFTRadiantItemBonus: "radiant-bonus",
    TFTHeadliner: "headliner-bonus",
    trueDamage: "true-damage",
    ExperimentBonus: "experiment-bonus",
  };

  // Replace custom tags with spans
  for (const [tag, className] of Object.entries(replaceTags)) {
    const regex = new RegExp(`<${tag}>(.*?)</${tag}>`, "g");
    result = result.replace(
      regex,
      (match, p1) => `<span class="${className}">${p1}</span>`,
    );
  }

  // Break the string into parts for icons and custom spans
  const regexPattern = new RegExp(`(%i:[^%]+%)`, "g");
  const parts = result.split(regexPattern);
  return (
    <CustomTag
      {...rest}
      dangerouslySetInnerHTML={{
        __html: parts
          .map((part, index) => {
            // Check for an icon match
            const iconMatch = /%i:(.*?)%/.exec(part);
            if (iconMatch) {
              const textIcon = textIcons[iconMatch[1]];
              const imageURL = STAT_ICONS[iconMatch[1]];
              if (textIcon) {
                return t(textIcon);
              }
              if (imageURL) {
                return ReactDOMServer.renderToString(
                  <img
                    key={index}
                    src={imageURL}
                    width="16"
                    height="16"
                    className={iconStyle(iconSize)}
                  />,
                );
              }
              return "";
            }
            return part;
          })
          .join(""),
      }}
    ></CustomTag>
  );
};

const iconStyle = (iconSize) => css`
  display: inline;
  width: ${iconSize};
  height: ${iconSize};
  margin: 0 var(--sp-0_5);
  margin-bottom: calc(var(--sp-0_5) * -1);
`;

export default ParsedDescription;
